// overlays view module: see src/view/modules.ts for the hooks. Owned by the view-overlays builder.
//
//   lens ...... legend colours over the land, from setLens (lens.ts)
//   fog ....... parchment over stale land (fog.ts) and sea (seaVeil.ts), from setSnapshot
//   props ..... trees, peaks and buildings turn to sepia ink under the fog (featureFog.ts)
//   strategic . flat pastel map with ink icons, occupation and claim stripes, from setMode
//               (strategic.ts, cameraDrive.ts)
//
// Lens, fog, city marks and claim marks share one per-hex texture (overlayData.ts).
//
// unsourced() counts what these layers draw that carries no source id: the city marks of the strategic
// view (each City has its sources) and a lens whose result names none. The fog is the absence of a
// recent source, not a claim, and the freshness lens is drawn from the sources' own dates.

import type * as THREE from 'three/webgpu';
import type { ViewModule } from '../modules.ts';
import type { ViewMode } from '../api.ts';
import type { WorldSnapshot } from '../../world/api.ts';
import { createCameraDrive, type CameraDrive } from './cameraDrive.ts';
import { cityMarks, unsourcedMarks } from './cityMarks.ts';
import { claimMarks } from './claimMarks.ts';
import { createFeatureFog, type FeatureFog } from './featureFog.ts';
import { createFog } from './fog.ts';
import { createFogState, type FogState } from './fogState.ts';
import { createLens, type LensLayer } from './lens.ts';
import { CHANNEL, createOverlayData, type OverlayData } from './overlayData.ts';
import { createSeaVeil, type SeaVeil } from './seaVeil.ts';
import { createStrategic, type StrategicLayer } from './strategic.ts';

/** Once the strategic view is this far in, the 3D solids (scene objects by name) are not drawn. */
const HIDE_AT = 0.999;
const SOLIDS = ['features', 'sites', 'ocean'] as const;
/** The lens that maps how old the newest source is; it has no source of its own to name. */
const SOURCE_AGE_LENS = 'freshness';

function createOverlays(): ViewModule {
  let data: OverlayData | null = null;
  let lens: LensLayer | null = null;
  let fog: FogState | null = null;
  let veil: SeaVeil | null = null;
  let props: FeatureFog | null = null;
  let strategic: StrategicLayer | null = null;
  let drive: CameraDrive | null = null;
  let features: THREE.Object3D | null = null;
  let scene: THREE.Scene | null = null;
  let hidden: THREE.Object3D[] = [];
  let pending: WorldSnapshot | null = null;
  let mode: ViewMode = '3d';
  let bareMarks = 0;
  let bareLens = 0;
  const teardown: Array<() => void> = [];

  function showSolids(): void {
    for (const object of hidden.splice(0)) {
      object.visible = true;
    }
  }

  /** The 3D props, the site models and the ocean are not drawn once the strategic view is fully in. */
  function hideSolids(): void {
    if (!strategic || !scene) {
      return;
    }
    const wantHidden = strategic.current() >= HIDE_AT;
    if (!wantHidden) {
      showSolids();
      return;
    }
    if (hidden.length > 0) {
      return;
    }

    for (const name of SOLIDS) {
      const object = scene.getObjectByName(name);
      if (object?.visible) {
        object.visible = false;
        hidden.push(object);
      }
    }
  }

  /** City marks and claim marks of a snapshot, into the shared texture. */
  function writeMarks(target: OverlayData, snapshot: WorldSnapshot): void {
    target.write(CHANNEL.CITY, cityMarks(snapshot.cities));
    target.writeClaims(claimMarks(snapshot.claim));
    bareMarks = unsourcedMarks(snapshot.cities);
  }

  return {
    name: 'overlays',
    install(core) {
      data = createOverlayData();
      lens = createLens(data);
      fog = createFogState(data);
      strategic = createStrategic(data, lens.amount);
      drive = createCameraDrive(core.camera);
      const fogLayer = createFog(fog, lens.amount, strategic);
      veil = createSeaVeil(core.data, fog, lens.amount, lens.current, { node: strategic.amount, current: strategic.current });
      props = createFeatureFog(fog, lens.amount, strategic.amount);
      features = core.features.group;
      scene = core.engine.scene;

      strategic.set(mode);
      drive.set(mode);
      teardown.push(core.surface.register(strategic.layer));
      teardown.push(core.surface.register(lens.layer));
      teardown.push(core.surface.register(fogLayer.layer));
      core.engine.scene.add(veil.mesh);
      teardown.push(core.engine.onFrame((dt) => {
        lens?.update(dt);
        fog?.update(dt);
        strategic?.update(dt);
        drive?.update(dt);
        veil?.update();
        hideSolids();

        // Prop materials are built when their first instance shows.
        if (features) {
          props?.attach(features);
        }
      }));

      // A snapshot that arrived while the paper was loading.
      if (pending) {
        fog.setSnapshot(pending);
        writeMarks(data, pending);
        pending = null;
      }
    },
    warm() {
      veil?.warm();
      if (features) {
        props?.attach(features);
      }
    },
    setSnapshot(snapshot) {
      if (!fog) {
        pending = snapshot;
        return;
      }
      fog.setSnapshot(snapshot);
      if (data) {
        writeMarks(data, snapshot);
      }
    },
    setMode(next) {
      mode = next;
      strategic?.set(next);
      drive?.set(next);
    },
    setLens(next) {
      lens?.set(next);
      bareLens = next && next.sources.length === 0 && next.id !== SOURCE_AGE_LENS ? 1 : 0;
    },
    unsourced() {
      const marksShown = strategic && strategic.current() > 0 ? bareMarks : 0;
      return marksShown + bareLens;
    },
    dispose() {
      for (const off of teardown.splice(0)) {
        off();
      }
      props?.detach();
      showSolids();
      veil?.dispose();
      lens?.dispose();
      data?.dispose();
      veil = null;
      props = null;
      features = null;
      scene = null;
      strategic = null;
      drive = null;
      lens = null;
      fog = null;
      data = null;
    },
  };
}

export const overlays: ViewModule = createOverlays();
